import {
  Whatshot,
  AcUnit,
  WbSunny,
  NewReleases,
  Forum,
  Schedule,
  Phone,
  PhoneMissed,
  PhoneCallback,
  PauseCircle,
  Replay,
  RemoveCircle,
  Block,
  LabelImportant,
  HelpOutline,
  ErrorOutline,
  Timer,
  NotificationsNone,
  TrendingUp,
  TrendingDown,
  VisibilityOff,
  ThumbUp,
  ThumbDown,
  WarningAmber,
  QuestionMark,
  AssignmentLate,
  AssignmentReturned,
  AssignmentTurnedIn,
  EventAvailable,
  BusinessCenter,
  CheckCircle,
  EmojiEvents,
  AccessTime,
  EventBusy,
  EventNote,
  CallEnd,
  CallMade,
  CallReceived,
  CallSplit,
  Chat,
  ChatBubble,
  PersonOff,
  Security,
  Payments,
  Facebook,
  Google,
  Language,
  Instagram,
} from "@mui/icons-material";

// ============================================================================
// COLOR UTILITIES
// ============================================================================

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

const hexToLightBg = (hex) => {
  if (!hex || !hex.startsWith("#")) return "#F1F5F9";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = 0.85;
  return `rgb(${Math.round(r + (255 - r) * mix)}, ${Math.round(g + (255 - g) * mix)}, ${Math.round(b + (255 - b) * mix)})`;
};

// ============================================================================
// COLOR PALETTES & CONFIGURATIONS
// ============================================================================

const COLOR_PALETTE = [
  { bgcolor: "#F5F3FF", color: "#7C3AED" },
  { bgcolor: "#EFF6FF", color: "#2563EB" },
  { bgcolor: "#ECFDF5", color: "#059669" },
  { bgcolor: "#FEF3C7", color: "#D97706" },
  { bgcolor: "#FEE2E2", color: "#DC2626" },
  { bgcolor: "#FCE7F3", color: "#DB2777" },
  { bgcolor: "#E0E7FF", color: "#4F46E5" },
  { bgcolor: "#CFFAFE", color: "#0891B2" },
  { bgcolor: "#FFEDD5", color: "#EA580C" },
  { bgcolor: "#D1FAE5", color: "#047857" },
  { bgcolor: "#FEF9C3", color: "#CA8A04" },
  { bgcolor: "#F3E8FF", color: "#9333EA" },
];

const SOURCE_CONFIG = {
  facebook: { bgcolor: "#EFF6FF", color: "#1877F2", Icon: Facebook },
  google: { bgcolor: "#FEF2F2", color: "#EA4335", Icon: Google },
  instagram: { bgcolor: "#FDF2F8", color: "#E1306C", Icon: Instagram },
  website: { bgcolor: "#F0FDF4", color: "#22C55E", Icon: Language },
  default: { bgcolor: "#F1F5F9", color: "#64748B", Icon: Language },
};

const PRIORITY_CONFIG = {
  high: { label: "High", bgcolor: "#FEE2E2", color: "#DC2626" },
  medium: { label: "Medium", bgcolor: "#FEF3C7", color: "#D97706" },
  low: { label: "Low", bgcolor: "#DBEAFE", color: "#2563EB" },
  default: { label: "—", bgcolor: "#F8FAFC", color: "#94A3B8" },
};

const PRIORITY_TO_STATUS_MAP = {
  hot: "high", high: "high", high_intent: "high", highintent: "high", urgent: "high",
  warm: "medium", medium: "medium", interested: "medium",
  cold: "low", low: "low", low_intent: "low", lowintent: "low",
};

export const STATUS_OPTIONS = [
  // Lead Grade Statuses
  "new", "cold", "warm", "hot", "dropped",
  // Pre-Consultation Pipeline Statuses
  "qualified", "need_analysis", "booked",
  // Post-Consultation Pipeline Statuses
  "post_consult", "proposal_shared", "negotiation", "treatment_booked", "won", "dropped",
  // Original statuses (kept for backward compatibility)
  "contacted", "interested", "not_interested", "callback", "follow_up", "pending"
];

export const PRIORITY_OPTIONS = ["high", "medium", "low"];

// All available statuses for filtering (curated list of common statuses)
export const FILTER_STATUS_OPTIONS = [
  // Lead Grade Statuses
  "new", "cold", "warm", "hot", "dropped",
  // Pre-Consultation Pipeline Statuses
  "qualified", "need_analysis", "booked",
  // Post-Consultation Pipeline Statuses
  "post_consult", "proposal_shared", "negotiation", "treatment_booked", "won", "dropped",
  // Original statuses
  "contacted", "interested", "not_interested", "callback", "follow_up", "pending",
  "unreachable", "busy"
];

// ============================================================================
// NEW WORKFLOW STATUS DEFINITIONS
// ============================================================================

// Lead Grade Statuses (Grade Type)
const new_status = {
  label: "New",
  color: "#0051FF",
  icon: <NewReleases fontSize="small" />,
};

const cold_status = {
  label: "Cold",
  color: "#007A99",
  icon: <AcUnit fontSize="small" />,
};

const warm_status = {
  label: "Warm",
  color: "#C48F00",
  icon: <WbSunny fontSize="small" />,
};

const hot_status = {
  label: "Hot",
  color: "#D40000",
  icon: <Whatshot fontSize="small" />,
};

const dropped_grade = {
  label: "Dropped",
  color: "#4B5563",
  icon: <RemoveCircle fontSize="small" />,
};

// Pre-Consultation Pipeline Statuses (Pipeline Type)
const qualified_leads = {
  label: "Qualified Leads",
  color: "#059669",
  icon: <AssignmentTurnedIn fontSize="small" />,
};

const need_analysis = {
  label: "Need Analysis",
  color: "#0EA5E9",
  icon: <HelpOutline fontSize="small" />,
};

const booked = {
  label: "Booked",
  color: "#067A2E",
  icon: <EventAvailable fontSize="small" />,
};

const dropped_preconsult = {
  label: "Dropped",
  color: "#4B5563",
  icon: <RemoveCircle fontSize="small" />,
};

// Post-Consultation Pipeline Statuses (Pipeline Type)
const post_consult = {
  label: "Post-Consult",
  color: "#7C3AED",
  icon: <AssignmentTurnedIn fontSize="small" />,
};

const proposal_shared = {
  label: "Proposal-Shared",
  color: "#0284C7",
  icon: <LabelImportant fontSize="small" />,
};

const negotiation = {
  label: "Negotiation",
  color: "#9333EA",
  icon: <Chat fontSize="small" />,
};

const treatment_booked = {
  label: "Treatment-Booked",
  color: "#EA580C",
  icon: <EventAvailable fontSize="small" />,
};

const won_status = {
  label: "Won",
  color: "#0A7A0A",
  icon: <EmojiEvents fontSize="small" />,
};

const dropped_postconsult = {
  label: "Dropped",
  color: "#4B5563",
  icon: <RemoveCircle fontSize="small" />,
};

// ============================================================================
// ORIGINAL STATUS UI MAP
// ============================================================================

export const STATUS_UI_MAP = {
  // Lead Grade Statuses
  new: new_status,
  cold: cold_status,
  warm: warm_status,
  hot: hot_status,
  dropped: dropped_grade,

  // Pre-Consultation Pipeline Statuses
  qualified: qualified_leads,
  need_analysis,
  booked,

  // Post-Consultation Pipeline Statuses
  post_consult,
  proposal_shared,
  negotiation,
  treatment_booked,
  won: won_status,

  // Original statuses (kept for backward compatibility)
  contacted: {
    label: "Contacted",
    color: "#2563EB",
    icon: <Phone fontSize="small" />,
  },
  unreachable: {
    label: "Unreachable",
    color: "#6B7280",
    icon: <PhoneMissed fontSize="small" />,
  },
  callback: {
    label: "Callback",
    color: "#7C3AED",
    icon: <PhoneCallback fontSize="small" />,
  },

  interested: {
    label: "Interested",
    color: "#16A34A",
    icon: <Forum fontSize="small" />,
  },
  notinterested: {
    label: "Not Interested",
    color: "#9CA3AF",
    icon: <ThumbDown fontSize="small" />,
  },

  followup: {
    label: "Follow Up",
    color: "#F97316",
    icon: <Schedule fontSize="small" />,
  },
  followuplater: {
    label: "Follow Up Later",
    color: "#FB923C",
    icon: <Timer fontSize="small" />,
  },
  followupmissed: {
    label: "Follow Up Missed",
    color: "#DC2626",
    icon: <AssignmentLate fontSize="small" />,
  },

  pending: {
    label: "Pending",
    color: "#F59E0B",
    icon: <PauseCircle fontSize="small" />,
  },
  retry: {
    label: "Retry",
    color: "#7C3AED",
    icon: <Replay fontSize="small" />,
  },
  waiting: {
    label: "Waiting",
    color: "#FACC15",
    icon: <Timer fontSize="small" />,
  },

  busy: {
    label: "Busy",
    color: "#6B7280",
    icon: <PauseCircle fontSize="small" />,
  },
  switchedoff: {
    label: "Switched Off",
    color: "#9CA3AF",
    icon: <VisibilityOff fontSize="small" />,
  },
  ringing: {
    label: "Ringing",
    color: "#0EA5E9",
    icon: <NotificationsNone fontSize="small" />,
  },

  positive: {
    label: "Positive",
    color: "#22C55E",
    icon: <ThumbUp fontSize="small" />,
  },
  negative: {
    label: "Negative",
    color: "#EF4444",
    icon: <ThumbDown fontSize="small" />,
  },
  neutral: {
    label: "Neutral",
    color: "#A1A1AA",
    icon: <HelpOutline fontSize="small" />,
  },

  unqualified: {
    label: "Unqualified",
    color: "#EF4444",
    icon: <TrendingDown fontSize="small" />,
  },

  duplicate: {
    label: "Duplicate",
    color: "#9CA3AF",
    icon: <Block fontSize="small" />,
  },
  duplicatelead: {
    label: "Duplicate Lead",
    color: "#9CA3AF",
    icon: <Block fontSize="small" />,
  },
  invalid: {
    label: "Invalid",
    color: "#6B7280",
    icon: <ErrorOutline fontSize="small" />,
  },
  blocked: {
    label: "Blocked",
    color: "#991B1B",
    icon: <Block fontSize="small" />,
  },
  spam: {
    label: "Spam",
    color: "#991B1B",
    icon: <Block fontSize="small" />,
  },

  dropped: {
    label: "Dropped",
    color: "#4A4A4A",
    icon: <RemoveCircle fontSize="small" />,
  },
  abandoned: {
    label: "Abandoned",
    color: "#525252",
    icon: <RemoveCircle fontSize="small" />,
  },
  expired: {
    label: "Expired",
    color: "#7C2D12",
    icon: <AssignmentReturned fontSize="small" />,
  },

  enquiryonly: {
    label: "Enquiry Only",
    color: "#0EA5E9",
    icon: <QuestionMark fontSize="small" />,
  },
  pricerequested: {
    label: "Price Requested",
    color: "#0284C7",
    icon: <LabelImportant fontSize="small" />,
  },
  comparing: {
    label: "Comparing",
    color: "#6366F1",
    icon: <HelpOutline fontSize="small" />,
  },

  highintent: {
    label: "High Intent",
    color: "#16A34A",
    icon: <TrendingUp fontSize="small" />,
  },
  lowintent: {
    label: "Low Intent",
    color: "#9CA3AF",
    icon: <TrendingDown fontSize="small" />,
  },

  verified: {
    label: "Verified",
    color: "#059669",
    icon: <AssignmentTurnedIn fontSize="small" />,
  },
  unverified: {
    label: "Unverified",
    color: "#F59E0B",
    icon: <WarningAmber fontSize="small" />,
  },

  rescheduled: {
    label: "Rescheduled",
    color: "#D97706",
    icon: <Replay fontSize="small" />,
  },
  deferred: {
    label: "Deferred",
    color: "#A855F7",
    icon: <PauseCircle fontSize="small" />,
  },

  languageissue: {
    label: "Language Issue",
    color: "#6B7280",
    icon: <ErrorOutline fontSize="small" />,
  },
  wrongnumber: {
    label: "Wrong Number",
    color: "#DC2626",
    icon: <PhoneMissed fontSize="small" />,
  },
  voicemail: {
    label: "Voicemail",
    color: "#64748B",
    icon: <PhoneMissed fontSize="small" />,
  },

  testlead: {
    label: "Test Lead",
    color: "#94A3B8",
    icon: <HelpOutline fontSize="small" />,
  },
  internal: {
    label: "Internal",
    color: "#475569",
    icon: <LabelImportant fontSize="small" />,
  },
  referral: {
    label: "Referral",
    color: "#22C55E",
    icon: <ThumbUp fontSize="small" />,
  },

  booking: {
    label: "Booking",
    color: "#067A2E",
    icon: <EventAvailable fontSize="small" />,
  },
  consultancy: {
    label: "Consultancy",
    color: "#5A1E9A",
    icon: <BusinessCenter fontSize="small" />,
  },
  confirmation: {
    label: "Confirmation",
    color: "#006B78",
    icon: <CheckCircle fontSize="small" />,
  },
  won: {
    label: "Won",
    color: "#0A7A0A",
    icon: <EmojiEvents fontSize="small" />,
  },

  lead_received: {
    label: "Lead Received",
    color: "#2563EB",
    icon: <NewReleases fontSize="small" />,
  },

  lead_assigned: {
    label: "Lead Assigned",
    color: "#6366F1",
    icon: <LabelImportant fontSize="small" />,
  },

  lead_viewed: {
    label: "Lead Viewed",
    color: "#64748B",
    icon: <VisibilityOff fontSize="small" />,
  },

  dialing: {
    label: "Dialing",
    color: "#0EA5E9",
    icon: <CallMade fontSize="small" />,
  },

  ringing: {
    label: "Ringing",
    color: "#38BDF8",
    icon: <NotificationsNone fontSize="small" />,
  },

  connected: {
    label: "Connected",
    color: "#22C55E",
    icon: <Phone fontSize="small" />,
  },

  not_connected: {
    label: "Not Connected",
    color: "#EF4444",
    icon: <PhoneMissed fontSize="small" />,
  },

  callback: {
    label: "Callback",
    color: "#7C3AED",
    icon: <PhoneCallback fontSize="small" />,
  },

  busy: {
    label: "Busy",
    color: "#6B7280",
    icon: <CallEnd fontSize="small" />,
  },

  switched_off: {
    label: "Switched Off",
    color: "#9CA3AF",
    icon: <VisibilityOff fontSize="small" />,
  },

  voicemail: {
    label: "Voicemail",
    color: "#64748B",
    icon: <CallReceived fontSize="small" />,
  },

  wrong_number: {
    label: "Wrong Number",
    color: "#DC2626",
    icon: <CallSplit fontSize="small" />,
  },

  interested: {
    label: "Interested",
    color: "#16A34A",
    icon: <ThumbUp fontSize="small" />,
  },

  not_interested: {
    label: "Not Interested",
    color: "#9CA3AF",
    icon: <ThumbDown fontSize="small" />,
  },

  neutral: {
    label: "Neutral",
    color: "#A1A1AA",
    icon: <HelpOutline fontSize="small" />,
  },

  high_intent: {
    label: "High Intent",
    color: "#22C55E",
    icon: <TrendingUp fontSize="small" />,
  },

  low_intent: {
    label: "Low Intent",
    color: "#9CA3AF",
    icon: <TrendingDown fontSize="small" />,
  },

  follow_up: {
    label: "Follow Up",
    color: "#F97316",
    icon: <Schedule fontSize="small" />,
  },

  follow_up_later: {
    label: "Follow Up Later",
    color: "#FB923C",
    icon: <Timer fontSize="small" />,
  },

  follow_up_missed: {
    label: "Follow Up Missed",
    color: "#DC2626",
    icon: <AssignmentLate fontSize="small" />,
  },

  rescheduled: {
    label: "Rescheduled",
    color: "#D97706",
    icon: <Replay fontSize="small" />,
  },

  waiting: {
    label: "Waiting",
    color: "#FACC15",
    icon: <AccessTime fontSize="small" />,
  },

  on_hold: {
    label: "On Hold",
    color: "#6B7280",
    icon: <PauseCircle fontSize="small" />,
  },

  discussion: {
    label: "Discussion",
    color: "#6366F1",
    icon: <Forum fontSize="small" />,
  },

  proposal_shared: {
    label: "Proposal Shared",
    color: "#0284C7",
    icon: <LabelImportant fontSize="small" />,
  },

  negotiation: {
    label: "Negotiation",
    color: "#9333EA",
    icon: <Chat fontSize="small" />,
  },

  // Post-Consultation Pipeline Statuses
  post_consult: {
    label: "Post-Consult",
    color: "#7C3AED",
    icon: <AssignmentTurnedIn fontSize="small" />,
  },

  price_discussion: {
    label: "Price Discussion",
    color: "#7C3AED",
    icon: <ChatBubble fontSize="small" />,
  },

  booking: {
    label: "Booking",
    color: "#22C55E",
    icon: <EventAvailable fontSize="small" />,
  },

  appointment_no_show: {
    label: "No Show",
    color: "#9CA3AF",
    icon: <PersonOff fontSize="small" />,
  },

  payment_pending: {
    label: "Payment Pending",
    color: "#F59E0B",
    icon: <Payments fontSize="small" />,
  },

  payment_received: {
    label: "Payment Received",
    color: "#16A34A",
    icon: <Payments fontSize="small" />,
  },

  verification_failed: {
    label: "Verification Failed",
    color: "#DC2626",
    icon: <ErrorOutline fontSize="small" />,
  },

  duplicate: {
    label: "Duplicate",
    color: "#9CA3AF",
    icon: <Block fontSize="small" />,
  },

  invalid: {
    label: "Invalid",
    color: "#6B7280",
    icon: <ErrorOutline fontSize="small" />,
  },

  spam: {
    label: "Spam",
    color: "#991B1B",
    icon: <Block fontSize="small" />,
  },

  won: {
    label: "Won",
    color: "#0A7A0A",
    icon: <EmojiEvents fontSize="small" />,
  },

  dropped: {
    label: "Dropped",
    color: "#4B5563",
    icon: <RemoveCircle fontSize="small" />,
  },
};

const fallbackColor = (key) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 65%, 45%)`;
};

export const getStatusUI = (key) => {
  if (STATUS_UI_MAP[key]) return STATUS_UI_MAP[key];

  return {
    color: fallbackColor(key),
    icon: <LabelImportant fontSize="small" />,
  };
};

export const capitalize = (key) =>
  key
    .replace(/followup/g, "follow up")
    .replace(/notinterested/g, "not interested")
    .replace(/highintent/g, "high intent")
    .replace(/lowintent/g, "low intent")
    .replace(/pricerequested/g, "price requested")
    .replace(/wrongnumber/g, "wrong number")
    .replace(/duplicatelead/g, "duplicate lead")
    .replace(/switchedoff/g, "switched off")
    .replace(/languageissue/g, "language issue")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ============================================================================
// STYLE GETTER FUNCTIONS
// ============================================================================

export const getColorFromPalette = (str) => {
  if (!str) return COLOR_PALETTE[0];
  return COLOR_PALETTE[hashString(str.toLowerCase()) % COLOR_PALETTE.length];
};

export const getSourceStyles = (source) => {
  const s = (source || "").toLowerCase();
  for (const [key, config] of Object.entries(SOURCE_CONFIG)) {
    if (key !== "default" && s.includes(key)) {
      const { Icon, ...colors } = config;
      return { ...colors, icon: <Icon sx={{ fontSize: 16 }} /> };
    }
  }
  const { Icon, ...colors } = SOURCE_CONFIG.default;
  return { ...colors, icon: <Icon sx={{ fontSize: 16 }} /> };
};

export const getStatusStyles = (statusKey) => {
  const normalized = (statusKey || "new").toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
  const ui = getStatusUI(normalized);
  return {
    bgcolor: hexToLightBg(ui.color),
    color: ui.color,
    label: ui.label || capitalize(statusKey || "new"),
    icon: ui.icon,
  };
};

export const getPriorityStyles = (priority) => {
  const p = (priority || "").toLowerCase();
  return PRIORITY_CONFIG[p] || PRIORITY_CONFIG.default;
};

export const mapToPriority = (value) => {
  const v = (value || "").toLowerCase();
  return PRIORITY_TO_STATUS_MAP[v] || "";
};
